import type { RegimeModelVersion, RegimeModelVersionOverrides } from '@/types/appRouter';
// Direct component imports keep this card statically renderable in tests: the UI barrel transitively
// pulls in the tRPC client, which needs a browser window.
import Callout from '@/components/UI/Callout';
import Card from '@/components/UI/Card';
import FormSelect from '@/components/UI/FormSelect';
import {
    applyRegimePinSelection,
    buildRegimePinEditorRows,
    listRegimePinDescriptions,
    type RegimePinHorizonCell
} from '../regimePinsPresentation';

interface RegimeModelPinsCardProps {
    /** Staged pins from the editor form state; `null` when the strategy pins nothing. */
    pins: RegimeModelVersionOverrides | null;
    /** Model catalog from trading requisites; `null` when it failed to load. */
    regimeModelVersions: readonly RegimeModelVersion[] | null;
    isCatalogLoading: boolean;
    onChange: (nextPins: RegimeModelVersionOverrides | null) => void;
}

const followActiveLabel = (cell: RegimePinHorizonCell): string => {
    if (cell.hasActiveConflict) return 'Follow active (conflicted)';

    return cell.activeVersionId === null ? 'Follow active (none active)' : `Follow active (v${cell.activeVersionId})`;
};

/**
 * Hints ride FormSelect's own `error`/`helpText` props so they are `aria-describedby`-associated with
 * the select rather than being bare sibling paragraphs.
 */
const cellHint = (cell: RegimePinHorizonCell): { error?: string; helpText?: string } => {
    if (cell.isPinnedVersionUnknown) {
        return { error: 'Pinned version is not in the model catalog' };
    }

    if (cell.hasActiveConflict) {
        return { helpText: 'Multiple active models for this horizon' };
    }

    if (cell.pinState === 'pinnedOverride') {
        return {
            helpText: cell.activeVersionId === null ? 'Overrides (no active model)' : `Overrides active v${cell.activeVersionId}`
        };
    }

    if (cell.pinState === 'pinnedToActive') {
        return { helpText: 'Pinned to the current active' };
    }

    return {};
};

/** Read-only fallback when the model catalog is unavailable: shows the real pins without offering edits that could clear them. */
const ReadOnlyPinList = ({ pins }: { pins: RegimeModelVersionOverrides | null }) => {
    const pinLines = listRegimePinDescriptions(pins);

    if (pinLines.length === 0) {
        return <p className="text-sm text-zinc-500">No pins — every scope resolves its active model.</p>;
    }

    return (
        <ul className="space-y-1 text-sm text-zinc-300">
            {pinLines.map((line) => (
                <li key={line}>{line}</li>
            ))}
        </ul>
    );
};

/**
 * Surface 4 of the regime dependence profile plan: which classifier version this strategy resolves per
 * scope and horizon. Pins take precedence over the active model wherever the strategy resolves regime
 * classifications and are bound into corpus fingerprints, so the save flow guards pin changes behind an
 * explicit informed confirmation — this card only stages them.
 */
const RegimeModelPinsCard = ({ pins, regimeModelVersions, isCatalogLoading, onChange }: RegimeModelPinsCardProps) => (
    <Card
        subtitle="Pins freeze which regime classifier version this strategy resolves per scope and horizon. A pin takes precedence over the active model for discovery and live classification and is bound into corpus fingerprints."
        title="Regime model pins"
    >
        {isCatalogLoading ? (
            <p className="text-sm text-zinc-500">Loading regime model catalog…</p>
        ) : regimeModelVersions === null ? (
            <div className="flex flex-col gap-3">
                <Callout title="Model catalog unavailable" variant="warning">
                    The regime model catalog failed to load, so pins are shown read-only. Reload the page to edit them.
                </Callout>
                <ReadOnlyPinList pins={pins} />
            </div>
        ) : (
            <div className="flex flex-col gap-4">
                {buildRegimePinEditorRows({ pins, regimeModelVersions }).map((row) => (
                    <div key={row.scope}>
                        <div className="mb-1.5 text-sm font-medium text-zinc-300">{row.label}</div>
                        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                            {row.cells.map((cell) => {
                                const hint = cellHint(cell);

                                return (
                                    <FormSelect
                                        aria-label={`${row.label} ${cell.horizon} regime model pin`}
                                        error={hint.error}
                                        helpText={hint.helpText}
                                        key={cell.horizon}
                                        label={cell.horizon}
                                        noMargin
                                        options={[
                                            { label: followActiveLabel(cell), value: '' },
                                            ...cell.options.map((option) => ({ label: option.label, value: option.versionId }))
                                        ]}
                                        size="sm"
                                        value={cell.pinnedVersionId ?? ''}
                                        onChange={(event) =>
                                            onChange(
                                                applyRegimePinSelection(
                                                    pins,
                                                    row.scope,
                                                    cell.horizon,
                                                    event.target.value === '' ? null : Number(event.target.value)
                                                )
                                            )
                                        }
                                    />
                                );
                            })}
                        </div>
                    </div>
                ))}
                <div className="space-y-1 text-xs text-zinc-500">
                    <p>The O&rsquo;Neil market direction framework is not pinnable — it always resolves its active version.</p>
                    <p>
                        Changing pins while experiments or regime profiles exist under the old resolution breaks comparability with that
                        prior work. Saving a pin change asks for explicit confirmation.
                    </p>
                </div>
            </div>
        )}
    </Card>
);

export default RegimeModelPinsCard;
